import React, { useEffect, useRef, useState } from 'react';
import { NodeKind, TaskItem } from '../../types/tasks';
import { useTasks } from '../../context/TasksContext';
import { floats, hasConflict, isCritical, isLate, isScheduled, planDays, rangeLabel } from '../../utils/projectPlan';
import { daysBetween } from '../../utils/taskDates';
import TaskIcon from '../tasks/TaskIcon';

const KINDS: { value: NodeKind; label: string }[] = [
  { value: 'task', label: 'Task' },
  { value: 'milestone', label: 'Milestone' },
  { value: 'event', label: 'Event' },
];

/**
 * The ⋯ menu's Schedule panel for a project topic: what it is (task,
 * milestone, event) and the dates people fix. Leave a date empty and the
 * scheduler works it out from what the task waits for. Clicking away keeps
 * what was typed, as the other panels do.
 */
const SchedulePanel: React.FC<{ item: TaskItem; readOnly: boolean; onClose: () => void; onOpenDeps: () => void }> = ({
  item,
  readOnly,
  onClose,
  onOpenDeps,
}) => {
  const { updateItem } = useTasks();
  const waits = (item.predecessors || []).length;
  const [kind, setKind] = useState<NodeKind>(item.node_kind === 'material' ? 'task' : item.node_kind || 'task');
  // An event that waits for something floats (projectPlan.floats): its start
  // comes from what it waits for, so it's set by how long it lasts. Dates it
  // had from before only carry over as its length.
  const floating = kind === 'event' && waits > 0;
  const hadSpan = floats(item) && !!item.start_date && !!item.due_date;
  const [start, setStart] = useState(item.start_date || '');
  const [due, setDue] = useState(hadSpan ? '' : item.due_date || '');
  const [days, setDays] = useState(
    item.duration_days
      ? String(item.duration_days)
      : hadSpan
        ? String(daysBetween(item.start_date!, item.due_date!) + 1)
        : ''
  );

  const patch = () => {
    const p: Partial<TaskItem> = {};
    const nextKind = item.node_kind === 'material' && kind === 'task' ? 'material' : kind;
    if (nextKind !== (item.node_kind || 'task')) p.node_kind = nextKind;
    // A milestone is one date (its due date); an event runs start → end,
    // unless it floats, when it has a length instead.
    const nextStart = kind === 'milestone' || floating ? null : start || null;
    const nextDays = (kind === 'task' || floating) && days ? Math.max(1, Math.min(999, Math.round(Number(days)) || 1)) : null;
    if (nextStart !== (item.start_date || null)) p.start_date = nextStart;
    if ((due || null) !== (item.due_date || null)) p.due_date = due || null;
    if (nextDays !== (item.duration_days ?? null)) p.duration_days = nextDays;
    return p;
  };
  const save = () => {
    const p = patch();
    if (Object.keys(p).length) updateItem(item.id, p);
  };

  // Click-away saves too.
  const handled = useRef(false);
  const latest = useRef(save);
  latest.current = save;
  useEffect(
    () => () => {
      if (!handled.current && !readOnly) latest.current();
    },
    [readOnly]
  );
  const finish = (fn: () => void) => () => {
    handled.current = true;
    fn();
    onClose();
  };

  const planned = isScheduled(item);

  return (
    <div className="tk-menu-panel tp-plan">
      <div className="tk-menu-title">Schedule</div>
      <div className="tk-segmented tk-segmented-sm tp-plan-kind" role="group" aria-label="Type">
        {KINDS.map((k) => (
          <button key={k.value} type="button" className={kind === k.value ? 'is-active' : ''} onClick={() => setKind(k.value)} disabled={readOnly}>
            {k.label}
          </button>
        ))}
      </div>

      {floating && <p className="tk-menu-hint tp-plan-floats">Starts once what it waits for is done.</p>}
      {kind !== 'milestone' && !floating && (
        <label className="tk-field">
          <span className="tk-field-label">{kind === 'event' ? 'Starts' : 'Starts on'} <span className="tk-muted">{kind === 'event' ? '' : '(empty = as soon as it can)'}</span></span>
          <input type="date" className="tk-input" value={start} onChange={(e) => setStart(e.target.value)} disabled={readOnly} />
        </label>
      )}
      {(kind === 'task' || floating) && (
        <label className="tk-field">
          <span className="tk-field-label">{floating ? 'Lasts' : 'Takes'}</span>
          <span className="tp-plan-days">
            <input type="number" min={1} max={999} className="tk-input" value={days} placeholder="1" onChange={(e) => setDays(e.target.value)} disabled={readOnly} />
            <span className="tk-muted">days</span>
          </span>
        </label>
      )}
      <label className="tk-field">
        <span className="tk-field-label">
          {kind === 'milestone' ? 'On' : floating ? 'Must happen by' : kind === 'event' ? 'Ends' : 'Due by'}{' '}
          <span className="tk-muted">
            {kind === 'task' ? '(deadline — never moves)' : floating ? '(deadline, optional)' : kind === 'milestone' ? '(empty = when what it waits for is done)' : ''}
          </span>
        </span>
        <input type="date" className="tk-input" value={due} min={kind === 'event' && !floating && start ? start : undefined} onChange={(e) => setDue(e.target.value)} disabled={readOnly} />
      </label>

      <button type="button" className="tk-menu-item tp-plan-waits" onClick={() => { handled.current = true; save(); onOpenDeps(); }}>
        <TaskIcon name="waits" size={15} />
        {waits ? `Waits for ${waits} task${waits === 1 ? '' : 's'}` : 'Waits for…'}
        <TaskIcon name="chevron-right" size={14} className="tp-plan-go" />
      </button>

      {planned && item.progress < 100 && (
        <p className={`tp-plan-info${isLate(item) ? ' is-late' : ''}${isCritical(item) ? ' is-critical' : ''}`}>
          Planned {rangeLabel(item.plan_start!, item.plan_finish!)}
          {planDays(item) > 0 && ` · ${planDays(item)}d`}
          {item.slack_days != null && item.node_kind !== 'milestone' && item.node_kind !== 'event' &&
            (isLate(item) ? ` · ${-item.slack_days}d late` : item.slack_days === 0 ? ' · no time to spare' : ` · ${item.slack_days}d to spare`)}
          {isCritical(item) && ' · on the critical path'}
        </p>
      )}

      {hasConflict(item) && (
        <p className="tp-plan-info is-conflict">
          Conflict: it's set to start {item.conflict_days} day{item.conflict_days === 1 ? '' : 's'} before what it waits for is done.
          Clear "Starts on" to let it follow, or move what it waits for.
        </p>
      )}

      {!readOnly && (
        <div className="tk-menu-actions">
          {(item.start_date || item.due_date || item.duration_days) && (
            <button
              type="button"
              className="tk-btn tk-btn-ghost"
              onClick={finish(() => updateItem(item.id, { start_date: null, due_date: null, duration_days: null }))}
              title="Clear the dates and duration (links stay)"
            >
              Clear
            </button>
          )}
          <button type="button" className="tk-btn tk-btn-primary" onClick={finish(save)}>Save</button>
        </div>
      )}
    </div>
  );
};

export default SchedulePanel;
